import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  LayoutDashboard,
  Building2,
  MessageSquare,
  FileText,
  LogOut,
  Search,
  Bell,
  Settings,
  Moon,
  Sun,
  User,
  ChevronLeft,
  ChevronRight,
  Home,
  X
} from 'lucide-react';
import { inquiryService } from '../../services/inquiryService';
import { Theme } from '@radix-ui/themes';

const AdminLayout = () => {
  const { user, isLoading, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();


  const [newInquiries, setNewInquiries] = useState([]);
  const [isNotificationsDropdownOpen, setIsNotificationsDropdownOpen] = useState(false);
 const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768; 
    }
    return true; 
  });

  const [themeAppearance, setThemeAppearance] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('radix-themes-appearance') || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('radix-themes-appearance', themeAppearance);

    }
  }, [themeAppearance]);

  const fetchNewInquiries = async () => {
    try {
      const inquiries = await inquiryService.getNewInquiriesLast24Hours();
      setNewInquiries(inquiries);
    } catch (error) {
      console.error('Failed to fetch new inquiries:', error);
    }
  };

  useEffect(() => {
    fetchNewInquiries();

    const intervalId = setInterval(fetchNewInquiries, 60000);

    return () => clearInterval(intervalId);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/antacres-aadashminx" state={{ from: location }} replace />;
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/antacres-aadashminx');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleInquiryClick = () => {
    setIsNotificationsDropdownOpen(false);
    navigate('/aadashmin/inquiries');
  };

  const navigationItems = [
    { path: '/aadashmin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/aadashmin/properties', icon: Building2, label: 'Properties' },
    { path: '/aadashmin/inquiries', icon: MessageSquare, label: 'Inquiries' },
    { path: '/aadashmin/content', icon: FileText, label: 'Content' },
    { path: '/aadashmin/settings', icon: Settings, label: 'Settings' },
    { path: '/', icon: Home, label: 'View Website' },
  ];

  const isActive = (path) => {
    if (path === '/') {
        return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleTheme = () => {
    setThemeAppearance(prevAppearance => (prevAppearance === 'dark' ? 'light' : 'dark'));
  };

  return (

    <Theme appearance={themeAppearance}>
      <div className="flex h-screen bg-gray-100 dark:bg-black overflow-hidden">
        <aside
          className={`${
            isSidebarOpen ? 'w-64' : 'w-20'
          } bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out flex flex-col flex-shrink-0 h-full shadow-lg z-20`}
        >
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/images/properties/luxury-villa-goa.jpg" alt="Antacres Logo" className="h-9 w-9 rounded-full object-cover border border-gray-100 dark:border-gray-800" />
              {isSidebarOpen && (
                <h1 className="font-extrabold text-2xl text-gray-900 dark:text-white tracking-tightest">Antacres</h1>
              )}
            </div>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-3.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 flex items-center justify-center"
              aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
            >
              {isSidebarOpen ? (
                <ChevronLeft className="h-5 w-5" />
              ) : (
                <ChevronRight className="h-5 w-5" />
              )}
            </button>
          </div>

          <nav className="flex-1 py-5 px-3 overflow-y-auto custom-scrollbar">
            {navigationItems.map(({ path, icon: Icon, label }) => (
              <Link
                key={path}
                to={path}
                className={`flex items-center px-4 py-3 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 mb-2 transition-all duration-200 ease-in-out
                  ${isActive(path)
                    ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400 font-semibold shadow-sm'
                    : 'hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
              >
                <Icon className="h-5 w-5 shrink-0 text-gray-600 dark:text-gray-300" />
                {isSidebarOpen && <span className="ml-3 whitespace-nowrap">{label}</span>}
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  className="flex items-center gap-3 p-2.5 w-full rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
                  aria-label="User menu"
                >
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-800 flex items-center justify-center shrink-0">
                    <User className="h-6 w-6 text-primary-600 dark:text-primary-200" />
                  </div>
                  {isSidebarOpen && (
                    <div className="text-left overflow-hidden">
                      <p className="text-base font-semibold text-gray-800 dark:text-gray-200 truncate">{user?.name || 'Admin User'}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">Administrator</p>
                    </div>
                  )}
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="min-w-[220px] bg-gray-100 dark:bg-gray-900 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 py-2 mt-2 z-50 animate-fade-in"
                  sideOffset={10}
                  align="start"
                >
                  <DropdownMenu.Item
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer outline-none transition-colors"
                    onSelect={() => navigate('/aadashmin/settings')} 

                  >
                    <Settings className="h-4 w-4 mr-3 text-gray-500 dark:text-gray-400" />
                    Settings
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="h-[1px] bg-gray-100 dark:bg-gray-700 mx-2 my-1" />
                  <DropdownMenu.Item
                    className="flex items-center px-4 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-red-50 dark:hover:bg-red-900 cursor-pointer outline-none transition-colors"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-3 text-primary-600 dark:text-primary-400" />
                    Sign Out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
          <header className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 shadow-sm">
            <div className="flex items-center justify-between px-6 py-3.5">
              <div className="flex-1 max-w-lg relative">
                <input
                  type="text"
                  placeholder="Search properties, inquiries, content..."
                  className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm placeholder-gray-400 dark:placeholder-gray-500
                    focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent transition-all"
                />
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-gray-400 dark:text-gray-500" />
              </div>

              <div className="flex items-center gap-3">
                <DropdownMenu.Root open={isNotificationsDropdownOpen} onOpenChange={setIsNotificationsDropdownOpen}>
                  <DropdownMenu.Trigger asChild>
                    <button
                      className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative text-gray-600 dark:text-gray-300"
                      title="Notifications"
                      aria-label="Notifications"
                    >
                      <Bell className="h-5 w-5" />
                      {newInquiries.length > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-primary-600 text-white text-xs font-bold rounded-full border-2 border-white dark:border-gray-950 flex items-center justify-center transform scale-90 origin-top-right">
                          {newInquiries.length}
                        </span>
                      )}
                    </button>
                  </DropdownMenu.Trigger>

                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      className="min-w-[320px] max-w-[400px] bg-gray-100 dark:bg-gray-600 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 py-3 mt-2 z-50 animate-fade-in-down"
                      sideOffset={10}
                      align="end"
                    >
                      <div className="px-4 pb-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">New Inquiries</p>
                        <p className="text-sm text-gray-600 dark:text-gray-200 mt-1">Received in the last 24 hours.</p>
                      </div>

                      {newInquiries.length > 0 ? (
                        <ul className="max-h-80 overflow-y-auto custom-scrollbar pt-2">
                          {newInquiries.map((inquiry) => (
                            <DropdownMenu.Item
                              key={inquiry.id}
                              className="flex flex-col items-start px-4 py-3 cursor-pointer outline-none transition-colors duration-200 hover:bg-blue-50 dark:hover:bg-blue-800 group"
                              onSelect={handleInquiryClick}
                            >
                              <p className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-200">
                                {inquiry.name || inquiry.email}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-200 mt-1">
                                Type: <span className="capitalize font-medium">{inquiry.type}</span>
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-300 mt-0.5">
                                Received: {new Date(inquiry.created_at).toLocaleString()}
                              </p>
                              {inquiry.property_id && (
                                <p className="text-xs text-gray-500 dark:text-gray-300 mt-0.5">Property ID: {inquiry.property_id}</p>
                              )}
                            </DropdownMenu.Item>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-gray-500 dark:text-gray-300 text-center py-8 px-4 text-sm">
                          <MessageSquare className="h-10 w-10 text-gray-300 dark:text-gray-500 mx-auto mb-3" />
                          No new inquiries in the last 24 hours.
                        </div>
                      )}
                      <DropdownMenu.Separator className="h-[1px] bg-gray-100 dark:bg-gray-700 mx-2 my-2" />
                      <DropdownMenu.Item
                        className="text-primary-600 dark:text-primary-200 text-sm font-medium text-center py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer outline-none"
                        onSelect={() => {
                          setIsNotificationsDropdownOpen(false);
                          navigate('/aadashmin/inquiries');
                        }}
                      >
                        View All Inquiries
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>

                {/* Dark/Light mode toggle button */}
                <button
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-300"
                  title="Toggle dark mode"
                  onClick={toggleTheme}
                >
                  {themeAppearance === 'dark' ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                </button>
               
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-0 sm:p-6 bg-gray-100 dark:bg-gray-950">
            <Outlet />
          </main>
        </div>
      </div>
    </Theme>
  );
};

export default AdminLayout;